import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SettingsProvider, useSettings } from './context/SettingsContext';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import { Toaster } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';
import applaiLogo from './assets/applai-logo.png';
import './styles/global.css';

const AppContent = () => {
  const { user, loading } = useAuth();
  const { settings } = useSettings();
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', user ? settings.theme : 'light');
  }, [user, settings.theme]);

  // Sync path when user presses back/forward
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to) => {
    window.history.pushState({}, '', to);
    setPath(to);
  }, []);

  // Keep URL in sync with auth state
  useEffect(() => {
    if (loading) return;
    if (user && path !== '/app') {
      window.history.replaceState({}, '', '/app');
      setPath('/app');
    } else if (!user && path === '/app') {
      window.history.replaceState({}, '', '/');
      setPath('/');
    }
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-logo">
          <img src={applaiLogo} alt="ApplAI" className="loading-logo-img" />
          <span>Appl<span style={{color:"#14B8A6"}}>AI</span></span>
        </div>
        <div className="loading-spinner" />
      </div>
    );
  }

  // Logged-in users always see the dashboard (URL is /app)
  if (user) return <Dashboard />;

  // Password reset link
  const hasResetToken = window.location.search.includes('token=') && window.location.search.includes('email=');
  if (hasResetToken) return <AuthPage onBack={() => navigate('/')} />;

  // /login → register / sign-in page
  if (path === '/login') return <AuthPage onBack={() => navigate('/')} />;

  // Default: landing page (/ or any unknown path for unauthenticated users)
  return <LandingPage onLogin={() => navigate('/login')} />;
};

const App = () => (
  <AuthProvider>
    <SettingsProvider>
    <Toaster
      position="top-right"
      toastOptions={{
        style: {
          background: '#FFFFFF', color: '#111827',
          border: '1px solid #E2E6EE',
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontSize: '13.5px',
          boxShadow: '0 4px 20px rgba(17,24,39,0.10)',
        },
        success: { iconTheme: { primary: '#10B981', secondary: '#FFFFFF' } },
        error:   { iconTheme: { primary: '#EF4444', secondary: '#FFFFFF' } },
      }}
    />
    <AppContent />
    <Analytics />
    </SettingsProvider>
  </AuthProvider>
);

export default App;
